import type {
  Account,
  Data,
  Job,
  Role,
  Service,
  User,
} from "@eai-toolkit/client";
import {
  dehydrateAccountsQueryStateToString,
  dehydrateDataQueryStateToString,
  dehydrateJobsQueryStateToString,
  dehydrateRolesQueryStateToString,
  dehydrateServicesQueryStateToString,
  dehydrateUsersQueryStateToString,
} from "@store/queryState";
import { useMemo } from "react";

import {
  useGetAccountByIdQuery,
  useGetAccountsQuery,
} from "../../store/api/accountsApi";
import {
  useGetDataObjectByIdQuery,
  useGetDataObjectsQuery,
} from "../../store/api/dataApi";
import { useGetJobByIdQuery, useGetJobsQuery } from "../../store/api/jobsApi";
import { RESOURCE_NAME_FIELD } from "../../store/api/resourceTypes";
import {
  useGetRoleByIdQuery,
  useSearchRolesQuery,
} from "../../store/api/rolesApi";
import {
  useGetServiceByIdQuery,
  useGetServicesQuery,
} from "../../store/api/servicesApi";
import {
  useGetUserByIdQuery,
  useSearchUsersQuery,
} from "../../store/api/usersApi";
import { stripNamespacePrefix } from "../../store/utils/queryStateUtils";
import type {
  BaseQueryState,
  FieldFilters,
} from "../../store/utils/queryStateUtils";
import type { ResourceItem } from "./ResourceSelector";

/** Shared with ResourceSelector.tsx, which gates search on the same threshold. */
export const MIN_SEARCH_LENGTH = 2;
const MAX_SEARCH_RESULTS = 25;

/** Whether/how a resource type is currently being queried. */
export interface TypeSearchArgs {
  debouncedQuery: string;
  isNameSearch: boolean;
  isIdSearch: boolean;
  isActive: boolean;
}

export interface TypeSearchResult {
  items: ResourceItem[];
  isFetching: boolean;
}

function buildSearchQueryState<T extends object>(
  query: string,
  nameField: string & keyof T,
): BaseQueryState<T> {
  const filters = new Map<string & keyof T, FieldFilters>();
  if (query.length >= MIN_SEARCH_LENGTH) {
    filters.set(nameField, {
      fuzzyMatch: query,
      valueList: [],
      booleanValueList: [],
      range: [],
    });
  }
  return {
    query: {
      filters,
      sort: { fieldName: nameField, direction: "asc" },
    },
    paging: { offset: 0, limit: MAX_SEARCH_RESULTS },
  };
}

/**
 * For ID search we verify the returned id matches what was searched, so a
 * slow/failed response can never surface a mismatched result.
 */
function matchesIdSearch<T extends { id?: string }>(
  item: T | undefined,
  debouncedQuery: string,
): item is T {
  return item?.id === debouncedQuery;
}

export function useDataSearch({
  debouncedQuery,
  isNameSearch,
  isIdSearch,
  isActive,
}: TypeSearchArgs): TypeSearchResult {
  const queryKey = useMemo(() => {
    const queryState = buildSearchQueryState<Data>(
      debouncedQuery,
      RESOURCE_NAME_FIELD.data,
    );
    return dehydrateDataQueryStateToString(queryState);
  }, [debouncedQuery]);

  const { data: response, isFetching: isFetchingSearch } =
    useGetDataObjectsQuery({ queryKey }, { skip: !isNameSearch || !isActive });
  const { data: byId, isFetching: isFetchingById } = useGetDataObjectByIdQuery(
    { id: debouncedQuery },
    { skip: !isIdSearch || !isActive },
  );

  return useMemo(() => {
    if (isNameSearch)
      return { items: response?.items ?? [], isFetching: isFetchingSearch };
    if (isIdSearch)
      return {
        items: matchesIdSearch(byId, debouncedQuery) ? [byId] : [],
        isFetching: isFetchingById,
      };
    return { items: [], isFetching: false };
  }, [
    isNameSearch,
    isIdSearch,
    response,
    byId,
    debouncedQuery,
    isFetchingSearch,
    isFetchingById,
  ]);
}

export function useJobSearch({
  debouncedQuery,
  isNameSearch,
  isIdSearch,
  isActive,
}: TypeSearchArgs): TypeSearchResult {
  const queryKey = useMemo(() => {
    const queryState = buildSearchQueryState<Job>(
      stripNamespacePrefix(debouncedQuery),
      RESOURCE_NAME_FIELD.job,
    );
    return dehydrateJobsQueryStateToString(queryState);
  }, [debouncedQuery]);

  const { data: response, isFetching: isFetchingSearch } = useGetJobsQuery(
    { queryKey },
    { skip: !isNameSearch || !isActive },
  );
  const { data: byId, isFetching: isFetchingById } = useGetJobByIdQuery(
    { jobId: debouncedQuery },
    { skip: !isIdSearch || !isActive },
  );

  return useMemo(() => {
    if (isNameSearch)
      return { items: response?.items ?? [], isFetching: isFetchingSearch };
    if (isIdSearch) {
      const job = byId?.data;
      return {
        items: matchesIdSearch(job, debouncedQuery) ? [job] : [],
        isFetching: isFetchingById,
      };
    }
    return { items: [], isFetching: false };
  }, [
    isNameSearch,
    isIdSearch,
    response,
    byId,
    debouncedQuery,
    isFetchingSearch,
    isFetchingById,
  ]);
}

export function useServiceSearch({
  debouncedQuery,
  isNameSearch,
  isIdSearch,
  isActive,
}: TypeSearchArgs): TypeSearchResult {
  const queryKey = useMemo(() => {
    const queryState = buildSearchQueryState<Service>(
      debouncedQuery,
      RESOURCE_NAME_FIELD.service,
    );
    return dehydrateServicesQueryStateToString(queryState);
  }, [debouncedQuery]);

  const { data: response, isFetching: isFetchingSearch } = useGetServicesQuery(
    { queryKey },
    { skip: !isNameSearch || !isActive },
  );
  const { data: byId, isFetching: isFetchingById } = useGetServiceByIdQuery(
    { id: debouncedQuery },
    { skip: !isIdSearch || !isActive },
  );

  return useMemo(() => {
    if (isNameSearch)
      return { items: response?.items ?? [], isFetching: isFetchingSearch };
    if (isIdSearch)
      return {
        items: matchesIdSearch(byId, debouncedQuery) ? [byId] : [],
        isFetching: isFetchingById,
      };
    return { items: [], isFetching: false };
  }, [
    isNameSearch,
    isIdSearch,
    response,
    byId,
    debouncedQuery,
    isFetchingSearch,
    isFetchingById,
  ]);
}

export function useUserSearch({
  debouncedQuery,
  isNameSearch,
  isIdSearch,
  isActive,
}: TypeSearchArgs): TypeSearchResult {
  const queryKey = useMemo(() => {
    const queryState = buildSearchQueryState<User>(
      debouncedQuery,
      RESOURCE_NAME_FIELD.user,
    );
    return dehydrateUsersQueryStateToString(queryState);
  }, [debouncedQuery]);

  const { data: response, isFetching: isFetchingSearch } = useSearchUsersQuery(
    { queryKey },
    { skip: !isNameSearch || !isActive },
  );
  const { data: byId, isFetching: isFetchingById } = useGetUserByIdQuery(
    { id: debouncedQuery },
    { skip: !isIdSearch || !isActive },
  );

  return useMemo(() => {
    if (isNameSearch)
      return { items: response?.items ?? [], isFetching: isFetchingSearch };
    if (isIdSearch)
      return {
        items: matchesIdSearch(byId, debouncedQuery) ? [byId] : [],
        isFetching: isFetchingById,
      };
    return { items: [], isFetching: false };
  }, [
    isNameSearch,
    isIdSearch,
    response,
    byId,
    debouncedQuery,
    isFetchingSearch,
    isFetchingById,
  ]);
}

export function useRoleSearch({
  debouncedQuery,
  isNameSearch,
  isIdSearch,
  isActive,
}: TypeSearchArgs): TypeSearchResult {
  const queryKey = useMemo(() => {
    const queryState = buildSearchQueryState<Role>(
      debouncedQuery,
      RESOURCE_NAME_FIELD.role,
    );
    return dehydrateRolesQueryStateToString(queryState);
  }, [debouncedQuery]);

  const { data: response, isFetching: isFetchingSearch } = useSearchRolesQuery(
    { queryKey },
    { skip: !isNameSearch || !isActive },
  );
  const { data: byId, isFetching: isFetchingById } = useGetRoleByIdQuery(
    { id: debouncedQuery },
    { skip: !isIdSearch || !isActive },
  );

  return useMemo(() => {
    if (isNameSearch)
      return { items: response?.items ?? [], isFetching: isFetchingSearch };
    if (isIdSearch)
      return {
        items: matchesIdSearch(byId, debouncedQuery) ? [byId] : [],
        isFetching: isFetchingById,
      };
    return { items: [], isFetching: false };
  }, [
    isNameSearch,
    isIdSearch,
    response,
    byId,
    debouncedQuery,
    isFetchingSearch,
    isFetchingById,
  ]);
}

export function useAccountSearch({
  debouncedQuery,
  isNameSearch,
  isIdSearch,
  isActive,
}: TypeSearchArgs): TypeSearchResult {
  const queryKey = useMemo(() => {
    const queryState = buildSearchQueryState<Account>(
      debouncedQuery,
      RESOURCE_NAME_FIELD.account,
    );
    return dehydrateAccountsQueryStateToString(queryState);
  }, [debouncedQuery]);

  const { data: response, isFetching: isFetchingSearch } = useGetAccountsQuery(
    { queryKey },
    { skip: !isNameSearch || !isActive },
  );
  const { data: byId, isFetching: isFetchingById } = useGetAccountByIdQuery(
    { id: debouncedQuery },
    { skip: !isIdSearch || !isActive },
  );

  return useMemo(() => {
    if (isNameSearch)
      return { items: response?.items ?? [], isFetching: isFetchingSearch };
    if (isIdSearch) {
      const account = byId?.account;
      return {
        items: matchesIdSearch(account, debouncedQuery) ? [account] : [],
        isFetching: isFetchingById,
      };
    }
    return { items: [], isFetching: false };
  }, [
    isNameSearch,
    isIdSearch,
    response,
    byId,
    debouncedQuery,
    isFetchingSearch,
    isFetchingById,
  ]);
}
